import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getContracts } from "../../api/contract/info";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import { formatDate } from "../../utils/date";
import Info_view from "./Info_view";
import PageHeader from "../../components/common/ui/PageHeader";

export default function Info() {
  const { id: projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const constructionType = queryParams.get("construction_type") || "";


  // 페이지 관리
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title"); // 기본 검색 필드
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTrigger, setSearchTrigger] = useState(0);


  const contract_type = "const";

  const [contracts, setContracts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [total, setTotal] = useState(0);

  const [selectedId, setSelectedId] = useState(null);

  // 필터 상태 (상태 버튼 / 분야 select)
  const [dateFilter, setDateFilter] = useState("updated_at"); // updated_at 등
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // 검색 실행
  const applySearch = () => {
    setPage(1);
    setSearchTrigger(prev => prev + 1);
  };

  // 계약 데이터 가져오기
  const fetchContracts = async () => {
    setLoading(true);
    try {
      const params = {
        project: projectId,
        construction_type: constructionType,
        [searchField]: q || undefined,

        // 날짜 필터 추가
        date_filter: dateFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,

        page,
        page_size: pageSize,
      };

      const res = await getContracts(projectId, params);
      setContracts(res.results ?? res);
      setTotal(res.count ?? res.total ?? res.length);
    } catch {
      setErrMsg("목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [projectId, constructionType, page, pageSize, searchTrigger]);

  // 검색
  const handleSearch = () => {
    setPage(1); // 검색하면 페이지 초기화
    setSearchTrigger((prev) => prev + 1); // 트리거 증가
  };

  // 엔터 입력 시 검색
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleRowClick = (contractId) => {
    setSelectedId(contractId);
  };

  const handleCreate = () => {
    navigate("new", { state: { construction_type: constructionType } });
  };

  const rows = useMemo(() => contracts ?? [], [contracts]);

  const columns = [
    {
      key: "contract_code",
      header: "코드",
      render: (row) => row.meta.contract_code,
    },
    {
      key: "construction_type",
      header: "공종",
      render: (row) => row.meta.construction_type,
    },
    {
      key: "confirmer_company_name",
      header: "주계약업체",
      render: (row) => row.meta.confirmer_company_name,
    },
    { key: "title", header: "계약명" },
    {
      key: "amount",
      header: "금액",
      render: (row) =>
        row.meta.amount
          ? `${Number(row.meta.amount).toLocaleString("ko-KR")} 원`
          : "-",
    },
    {
      key: "signed_date_actual",
      header: "계약 체결일",
      render: (row) =>
        row?.meta.signed_date_actual
          ? formatDate(row.meta.signed_date_actual)
          : "-",
    },
    {
      key: "end_date_expected",
      header: "종료 예정일",
      render: (row) =>
        row?.meta.end_date_expected
          ? formatDate(row.meta.end_date_expected)
          : "-",
    },
  ];

  return (
    <div className="mx-auto p-6 rounded-2xl bg-gray-50">
      {/* 헤더 */}
      <PageHeader>시공계약 관리</PageHeader>
      <div className="mb-6">
        <div className="flex items-center justify-between w-full">

          {/* 왼쪽은 비워두기 */}
          <div className="flex-1"></div>

          {/* 오른쪽: 검색 + 버튼 */}
          <div className="flex items-center gap-2">

            {/* 검색바 */}
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />

            {/* 새 계약 등록 버튼 */}
            <button
              onClick={handleCreate}
              className="btn-create whitespace-nowrap"
            >
              {constructionType} 새 계약 등록
            </button>

          </div>
        </div>

      </div>

      {/* 리스트 */}
      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={errMsg}
        emptyText="계약이 없습니다."
        onRowClick={(row) => handleRowClick(row.dns)}
      />

      {/* 페이지네이션 */}
      <div className="mt-2 mb-2">
        <BoardPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
        />
      </div>

      {/* 선택된 계약 */}
      {selectedId && (
        <div className="mt-4 border-t pt-6">
          <Info_view
            contractId={selectedId}
            onClose={() => setSelectedId(null)}
            onDeleted={() => {
              setSelectedId(null)
              fetchContracts();
            }}
            onUpdated={(newId) => {
              setSelectedId(newId);
              fetchContracts();
            }}
          />
        </div>
      )}
    </div>
  );
}
